import * as React from "react";
import { Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as VideoProductionIcon } from '../../../assets/svg/video-production.svg';
import { ReactComponent as CompleteIcon } from '../../../assets/svg/complete.svg';

interface Props {
  isActive?: boolean;
  onActiveClick?: () => void;
  onCompleteClick?: () => void;
  activeLabel?: string;
  completeLabel?: string;
  /**
   * Optional third segment for an admin-only Deleted view. Pass
   * `onDeletedClick` to show it; while `isDeleted` is true it is the selected
   * segment and neither Active nor Complete is.
   */
  isDeleted?: boolean;
  onDeletedClick?: () => void;
  deletedLabel?: string;
}

export default function ProjectsSwitcher({
  isActive = true,
  onActiveClick,
  onCompleteClick,
  activeLabel = "Active",
  completeLabel = "Final Delivered",
  isDeleted = false,
  onDeletedClick,
  deletedLabel = "Deleted",
}: Props) {
  const showDeleted = !!onDeletedClick;
  const deletedSelected = showDeleted && isDeleted;
  const activeSelected = isActive && !deletedSelected;
  const completeSelected = !isActive && !deletedSelected;

  const getProps = (isSelected: boolean, positionClass: string) => {
    // Three segments at 8rem each overflow a phone, so below sm the Deleted
    // variant sizes them to their content with tighter padding.
    const sizing = showDeleted
      ? "px-2 gap-1 min-w-0 sm:px-4 sm:gap-2 sm:min-w-[8rem]"
      : "px-4 gap-2 min-w-[8rem]";
    const base =
      `border py-3 ${sizing} flex justify-center items-center whitespace-nowrap cursor-pointer border-border-token transition-all`;
    const selectedStyle = "bg-primary bg-opacity-30";
    const unselectedStyle = "";

    return {
      className: `${base} ${positionClass} ${
        isSelected ? selectedStyle : unselectedStyle
      }`,
    };
  };

  const labelSx = (isSelected: boolean) => ({
    color: isSelected ? 'var(--accent-text)' : 'var(--text-2)',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: isSelected ? 900 : 450,
    lineHeight: 'normal',
  });

  return (
    <div className="flex rounded-xl overflow-hidden border-border-token">
      <div
        {...getProps(activeSelected, "rounded-l-xl")}
        onClick={onActiveClick}
      >
        <VideoProductionIcon className="w-4 h-4" 
          style={{ color: activeSelected ? "var(--accent-text)" : "var(--muted)" }}
          />
        <Typography sx={labelSx(activeSelected)}>
          {activeLabel}
        </Typography>
      </div>

      <div
        {...getProps(completeSelected, showDeleted ? "" : "rounded-r-xl")}
        onClick={onCompleteClick}
      >
        <CompleteIcon className="w-4 h-4" 
          style={{ color: completeSelected ? "var(--accent-text)" : "var(--muted)" }}
          />
        <Typography sx={labelSx(completeSelected)}>
          {completeLabel}
        </Typography>
      </div>

      {showDeleted && (
        <div
          {...getProps(deletedSelected, "rounded-r-xl")}
          onClick={onDeletedClick}
        >
          <DeleteOutlineIcon
            sx={{
              fontSize: 16,
              color: deletedSelected ? "var(--accent-text)" : "var(--muted)",
            }}
          />
          <Typography sx={labelSx(deletedSelected)}>
            {deletedLabel}
          </Typography>
        </div>
      )}
    </div>
  );
}
